import TableContainer from "../../Components/Table";
import { useEffect, useState } from "react";
import { getCompleted } from "../../api/common";
import { AUDIT_MODES, ERROR_MESSAGE } from "../../constants";
import ShowAttendees from "../../Containers/ShowAttendees";
import {
  calculateOffset,
  getAuditLevelLabel,
  getDateFormat,
} from "../../utils";
import styles from "./style.module.scss";
import Alert from "../../Components/AlertPopup";
import OrganizationList from "../../Components/OrgModal/OrganizationList";
import Info from "../../icons/infoIcon.svg?react";
import AuditInformationModal from "../../Components/InfoModal/AuditInformationModal";

const AuditCompleted = () => {
  const [loading, setLoading] = useState(false);
  const [rowData, setRowData] = useState<[]>([]);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [showModal, setShowModal] = useState(false);
  const [auditId, setAuditId] = useState("");
  const [pageSize, setPageSize] = useState<number>(10); // Default page size
  const [showInfo, setShowInfo] = useState(false);
  const [element, setElement] = useState([]);

  const columnData = [
    { label: "No", key: "no" },
    { label: "Audit ID", key: "auditCode" },
    {
      label: "Organization",
      key: "orgDetails",
      tdClassname: "text-start wrapTheme",
    },
    {
      label: "Application",
      key: "applicationList",
      tdClassname: "text-start wrapTheme",
    },
    { label: "Theme", key: "theme", tdClassname: "text-start wrapTheme" },
    { label: "Level", key: "auditLevel", tdClassname: "text-center" },
    {
      label: "Duration",
      key: "duration",
      tdClassname: "text-center",
      isMoreField: true,
    },
    { label: "Response Time", key: "responseTime", isMoreField: true },
    { label: "Reminder Alert", key: "escalationFrequency", isMoreField: true },
    { label: "Closed On", key: "closedOn", isMoreField: true },
    { label: "Info", key: "info", disableSortBy: true },
  ];

  const MoreFeildsValue = [
    { label: "Duration", value: "duration" },
    {
      label: "Response Time",
      value: "responseTime",
      tdClassname: "text-start",
    },
    { label: "Reminder Alert", value: "escalationFrequency" },
    { label: "Closed On", value: "closedOn", isMoreField: true },
  ];

  const getUniqueAppNames = (orgs: any[]) => {
    const names: string[] = [];
    orgs.forEach((o) =>
      o.applications?.forEach((a: any) => {
        if (!names.includes(a.applicationName)) names.push(a.applicationName);
      })
    );
    return names.join(", ");
  };

  useEffect(() => {
    // eslint-disable-next-line
    fetchData(1);
  }, []);
  const handleErrorClose = () => setShowErrorToast(false);

  const fetchData = async (pageNumber: number) => {
    setLoading(true);
    const finalSet: any = [];
    getCompleted(calculateOffset(pageNumber))
      .then((response) => {
        if (response) {
          let i = 1;
          response.forEach((element: any) => {
            const kickoffDate =
              element.kickOffFromDate && element.kickOffToDate
                ? `${getDateFormat(element.kickOffFromDate, "DD/MM/YYYY")} - ${getDateFormat(
                  element.kickOffFromDate,
                  "hh:mm A"
                )} to ${getDateFormat(element.kickOffToDate, "hh:mm A")}`
                : "N/A";
            finalSet.push({
              ...element,
              no: i,
              kickoffDate,
              auditMode: AUDIT_MODES[element.auditMode] || "Unknown",
              auditLevel: `${element.auditLevel} (${getAuditLevelLabel(element.auditLevel)})`,
              createdDate: getDateFormat(element.createdOn, "DD/MM/YYYY"),
              closedOn: getDateFormat(element.completedDate, "DD/MM/YYYY"),
              theme: element.themes
                .map((data: any) => data?.themeName)
                .join(", "),
              applicationList: getUniqueAppNames(element.organizations),
              duration: `${element.startDate?.split("-")?.reverse()?.join("/")} - ${element.endDate?.split("-")?.reverse()?.join("/")}`,
              escalationFrequency: element.escalationFrequency ? `${element.escalationFrequency} Days` : "-",
              responseTime: `${element.responseTime} Days`,
              orgDetails: (
                <OrganizationList
                  title="Organization Under Audit ID:"
                  auditCode={element.auditCode}
                  organizations={element.organizations}
                />
              ),
              info: (
                <span>
                  <Info
                    className={styles.iconStyleActions}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowInfo(true);
                      setElement(element);
                    }}
                  />
                </span>
              ),
              stage: (
                <span
                  className={`${styles.stageBadge} ${styles[element.stage?.toLowerCase().replace(" ", "-")] || styles.unknown}`}
                >
                  {element.stage || "-"}
                </span>
              ),
            });
            i++;
          });
        }
        setRowData(finalSet);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        setShowErrorToast(true);
      });
  };
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handlePageSizeChange = (newLimit: number) => {
    setPageSize(newLimit);
    setCurrentPage(1); // Reset to first page on limit change
  };
  const indexOfLastItem = currentPage * pageSize;
  const indexOfFirstItem = indexOfLastItem - pageSize;
  const currentClosure = rowData.slice(indexOfFirstItem, indexOfLastItem);
  return (
    <>
      <div>
        <TableContainer
          columnData={columnData}
          rowData={currentClosure}
          totalResult={rowData.length}
          enableMoreFeilds={true}
          MoreFeildsValue={MoreFeildsValue}
          title={"List of Completed Audits"}
          enableActionButton={false}
          enableFilter={true}
          itemsPerPage={pageSize}
          currentPage={currentPage}
          setCurrentPage={(pageNumber) => {
            setCurrentPage(pageNumber);
            fetchData(pageNumber);
          }}
          onPageNoChange={handlePageChange}
          onPageLimitChange={handlePageSizeChange}
          showLoading={loading}
          noRecordMessage={"There are no audits in the completed stage."}
        />
      </div>
      <Alert
        type="error"
        show={showErrorToast}
        onClose={handleErrorClose}
        message={ERROR_MESSAGE.API_ERROR_MESSAGE}
      />

      <ShowAttendees
        showModal={showModal}
        auditId={auditId}
        onClose={() => {
          setShowModal(false);
        }}
      />
      <AuditInformationModal
        element={element}
        show={showInfo}
        onHide={() => setShowInfo(false)}
      />
    </>
  );
};

export default AuditCompleted;
